import { ComponentPropsWithoutRef, memo } from 'react';

import { faCheck, faInfoCircle, faWarning } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as z from 'zod';

import { apiClient } from 'core/api/globals';
import { useDataStream, useEvent } from 'core/hooks';
import { Button, ComponentSizes, FilePromptButton, Icon, Page, PageHeader, ProgressBar, StatusBox, TextAreaField } from 'core/ui';

import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { useBasicUploadView } from 'features/file/hooks/useBasicUploadView';
import { Classification } from 'features/upload-pipeline';
import { useUploadPipeline } from 'features/upload-pipeline/hooks/useUploadPipeline';

export const UploadAlternativePage = memo(() => {
  const { activeScheme } = useAuthentication();
  const uploadPipeline = useUploadPipeline();
  const uploadView = useBasicUploadView();

  const rhfContext = useForm({
    defaultValues: { notes: '' },
    resolver: zodResolver(FormSchema),
  });

  const status = useDataStream(uploadView.streams.status);
  const uploadPercentage = status.totalBytes === 0 ? 0 : (100 * status.uploadedBytes) / status.totalBytes;

  // Status box logic and display values.
  const { statusIcon, statusLevel, statusMessage } =
    status.totalFiles === 0
      ? {
          statusIcon: null,
          statusLevel: 'info',
          statusMessage: '',
        }
      : status.totalFiles !== status.uploadedFiles
        ? {
            statusIcon: faInfoCircle,
            statusLevel: 'info',
            statusMessage: 'Uploading...',
          }
        : rhfContext.formState.isSubmitting
          ? {
              statusIcon: faInfoCircle,
              statusLevel: 'info',
              statusMessage: 'Requesting help...',
            }
          : rhfContext.formState.isSubmitSuccessful
            ? {
                statusIcon: faCheck,
                statusLevel: 'success',
                statusMessage: 'Files uploaded and help requested',
              }
            : {
                statusIcon: faWarning,
                statusLevel: 'warning',
                statusMessage: 'Waiting for request...',
              };

  const handleFormSubmit: SubmitHandler<FormValuesType> = useEvent(async (data) => {
    if (activeScheme == null) throw new Error('Authentication scheme is missing. Cannot send upload help request.');

    await apiClient.uploadTrackerClient.sendUploadHelpRequest(
      uploadPipeline.getUploadSessionId(),
      data.notes ?? null,
      activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
    );
  });

  const handleFilesSelected = useEvent((files: File[]) => {
    uploadPipeline.addFiles(
      files.map((file) => ({
        fileId: crypto.randomUUID(),
        file,
        classification: Classification.Alternative,
        metadata: null,
      })),
    );
  });

  return (
    <StyledPage>
      <PageHeader title="Alternative Upload" />
      <FormProvider {...rhfContext}>
        <StyledMainDiv>
          <StyledHeaderDiv>Alternative Upload</StyledHeaderDiv>

          {status.totalFiles === 0 && (
            <>
              <StyledP>
                If there are problems with the normal upload process then you can use this alternative upload method. This method is slower than the normal
                upload process, but it can be useful if you are having problems.
              </StyledP>

              <div>
                <FilePromptButton onFilesSelected={handleFilesSelected} directory>
                  Select Folder For Upload
                </FilePromptButton>
              </div>
            </>
          )}

          {status.totalFiles > 0 && (
            <>
              <StyledProgressBox>
                <StyledProgressBar value={uploadPercentage} size={ComponentSizes.LARGE} labelResolver={progressText} />

                <div>
                  Uploaded {status.uploadedFiles} of {status.totalFiles} {status.totalFiles === 1 ? 'file' : 'files'}
                </div>
              </StyledProgressBox>

              {(status.uploadedFiles !== status.totalFiles || !rhfContext.formState.isSubmitSuccessful) && (
                <StyledForm
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  noValidate
                  onSubmit={rhfContext.handleSubmit(handleFormSubmit)}
                >
                  <TextAreaField
                    name="notes"
                    label="Please provide any information that may help troubleshoot the issue."
                    disabled={rhfContext.formState.isSubmitting || rhfContext.formState.isSubmitSuccessful}
                    maxLength={1000}
                    required
                  />

                  <StyledSubmitButtonDiv>
                    <Button type="submit" disabled={rhfContext.formState.isSubmitting || rhfContext.formState.isSubmitSuccessful}>
                      Request Help
                    </Button>
                  </StyledSubmitButtonDiv>
                </StyledForm>
              )}

              <StyledStatusBox status={statusLevel as ComponentPropsWithoutRef<typeof StatusBox>['status']}>
                {statusIcon != null && <Icon icon={statusIcon} fixedWidth={false} />}
                {statusMessage}
              </StyledStatusBox>
            </>
          )}
        </StyledMainDiv>
      </FormProvider>
    </StyledPage>
  );
});

UploadAlternativePage.displayName = 'UploadAlternativePage';

const FormSchema = z.object({
  notes: z.string().min(1, 'Required'),
});

function progressText(value: number | null | undefined) {
  return value == null ? '' : `${Math.floor(value)} %`;
}

type FormValuesType = z.infer<typeof FormSchema>;

const StyledPage = styled(Page)`
  grid-template-rows: min-content min-content;
`;

const StyledMainDiv = styled.div`
  justify-self: center;
  display: grid;
  grid-template-columns: minmax(0, 420px);
  grid-auto-rows: min-content;
  row-gap: ${({ theme }) => theme.space.spacing40};
  justify-content: center;
  padding: ${({ theme }) => theme.space.spacing40} 0;
  background-color: ${({ theme }) => theme.colors.palette.white};
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.palette.blues[3]};
  padding: ${({ theme }) => theme.space.spacing40};
  margin-top: ${({ theme }) => theme.space.spacing40};
  justify-items: center;
`;

const StyledHeaderDiv = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.heading1};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  line-height: ${({ theme }) => theme.lineHeights.heading1};
  text-align: center;
  user-select: none;
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledP = styled.p`
  user-select: none;
`;

const StyledForm = styled.form`
  justify-self: stretch;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  row-gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledSubmitButtonDiv = styled.div`
  justify-self: center;
`;

const StyledProgressBox = styled.div`
  justify-self: stretch;
  display: grid;
  grid-template-columns: min-content min-content;
  grid-auto-rows: min-content;
  justify-content: space-between;
  white-space: nowrap;
`;

const StyledProgressBar = styled(ProgressBar)`
  grid-column: 1 / -1;
`;

const StyledStatusBox = styled(StatusBox)`
  justify-self: stretch;

  .icon-container {
    margin-right: ${({ theme }) => theme.space.spacing20};
  }
`;
